import styled from 'styled-components/macro';
import classNames from 'classnames';
import BuildingNavigation from './ui/building-navigation';
import { createRef, forwardRef, useEffect, useState } from 'react';
import { useStore } from './store';
import { buildings } from '../config/building-config';
import { BuildingType } from '../config/buildings';
import useTranslations from './utils/language-provider';
import { useParams } from 'react-router-dom';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import ProductPin from './ui/product-pin';
import AlternativeProductsPin from './ui/alternative-products-pin';
import { getOffsetPosition } from './utils/position';
import { products } from '../config/products';
import { PinchPanImg, PinchPan } from "./utils/pinch-pan-helpers";
import BuildingWrapper from './building-wrapper';
import { RouteParams } from '../interfaces/router';
import SecondPartArrow from './ui/second-part-arrow';
import { BuildingProducts } from '../interfaces/buildings';
import Breadcrumbs, { BreadcrumbsWrapper, ButtonsWrapper, StyledContactButton } from './ui/breadcrumbs';
import { isMobileDevice } from './utils/mobile-detection';
import FullscreenToggleButton from './ui/fullscreen-button';
import { ReactComponent as Contact } from '../assets/icon-contact.svg';

type BuildingDetailsProps = {
    type: BuildingType;
}

const StyledProductPin = styled(ProductPin)`
    position: absolute;
    --animate-duration: 700ms;
    opacity: 0;

    &.active {
        opacity: 1;
    }
`

const StyledAlternativeProductsPin = styled(AlternativeProductsPin)`
    position: absolute;
    --animate-duration: 700ms;
    opacity: 0;

    &.active {
        opacity: 1;
    }
`

const BuildingInner = styled.div`
    position: absolute;
    top: 0;
    max-width: 100%;

    * {
        max-width: 100%;
    }
`

const BuildingImage = styled.div`
    position: relative;
`

const BuildingDetails = forwardRef<HTMLDivElement, BuildingDetailsProps>(({ type }, ref) => {
    const {state} = useStore();
    const translations = useTranslations();
    const config = buildings[type] ;
    const sectors = config?.techSectors;
    const {techSector: techSectorParam = '', product: productParam} = useParams<RouteParams>();
    const [pinsActive, setPinsActive] = useState(false);
    const [currentAltProduct, setCurrentAltProduct] = useState<number>(0);
    const buildingInnerRef = createRef<HTMLDivElement>();

    const techSectorConfig = techSectorParam ? config.techSectors?.find(sector => sector.type === techSectorParam) : null;

    const imgPath = techSectorConfig?.img ? techSectorConfig.img : config.img || '';
    const productsConfig = techSectorConfig?.products || config.products;
    const altProductsConfig = techSectorConfig?.alternativeProducts || config.alternativeProducts;

    const secondParts = techSectorConfig?.secondParts;

    useEffect(() => {
        setTimeout(() => {
            setPinsActive(true);
        }, 1000)

    }, [type, translations.buildings]);

    const [rerender, setRerender] = useState(0);

    // Debug effect that allows easy repositioning of product pins via keyboard arrows
    // Enable by adding parameter ?debug-pins
    useEffect(() => {
        if (!new URLSearchParams(location.search).has('debug-pins')) {
            return;
        }

        let product: BuildingProducts | undefined;
        const onProductPopOver = (event: CustomEvent) => {
            product = productsConfig?.find(p => p.type === event.detail.productType);
        };

        const onKeyDown = (event: KeyboardEvent) => {
            event.preventDefault();
            event.stopImmediatePropagation();

            const amount = event.shiftKey ? 10 : 2;
            const mult = event.ctrlKey ? 10 : 1;
            if (!product) {
                return;
            }

            switch (event.key) {
                case 'ArrowUp':
                    product.position.y -= amount * mult;
                    break;
                case 'ArrowDown':
                    product.position.y += amount * mult;
                    break;
                case 'ArrowLeft':
                    product.position.x -= amount * mult;
                    break;
                case 'ArrowRight':
                    product.position.x += amount * mult;
                    break;
                default:
                    return;
            }

            setRerender(Date.now());
            console.log(product.type, `position: {x: ${product.position.x}, y: ${product.position.y}},`);
        };

        window.addEventListener('debug-product-popover', onProductPopOver as EventListener);
        window.addEventListener('keydown', onKeyDown);

        return () => {
            window.removeEventListener('debug-product-popover', onProductPopOver as EventListener);
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [productsConfig]);
    const mainBuilding = translations.buildings[type]?.mainBuilding;
    const building: { label: string; href: string } = mainBuilding?.building
        ? {
              label: translations.buildings[mainBuilding.building].name,
              href: `/${mainBuilding.building}`,
          }
        : {
              label: translations.buildings[type].name,
              href: `/${type}`,
          };
    const subBuilding = translations.buildings[type].mainBuilding
        ? {
              label: translations.buildings[type].name,
              href: `/${type}`,
          }
        : undefined;
    const techSektor = techSectorConfig
        ? {
              label: translations.sectors[techSectorConfig.type].name,
              href: `/${type}/${techSectorParam}`,
          }
        : undefined;
    const contactText = translations.contact?.text || 'Kontakt';
    const contactHref = translations.contact?.href || '/contact';
    return (
        <BuildingWrapper ref={ref}>
            <BuildingImage>
                <BreadcrumbsWrapper>
                    <Breadcrumbs building={building} subBuilding={subBuilding} techSektor={techSektor} />
                    <ButtonsWrapper>
                        {isMobileDevice() && <FullscreenToggleButton />}
                        <StyledContactButton href={contactHref} target='_blank'>
                            <Contact />
                            <p>{contactText}</p>
                        </StyledContactButton>
                    </ButtonsWrapper>
                </BreadcrumbsWrapper>
                <SwitchTransition mode='in-out'>
                    <CSSTransition
                        key={techSectorParam}
                        timeout={500}
                        classNames='page-fade'
                        nodeRef={buildingInnerRef}
                    >
                        <BuildingInner ref={buildingInnerRef}>
                            <PinchPan layer={3}>
                                <PinchPanImg
                                    src={
                                        altProductsConfig?.alternatives !== undefined
                                            ? altProductsConfig.alternatives[currentAltProduct].img
                                            : imgPath
                                    }
                                />

                                {secondParts?.map((secondPart, index) => (
                                    <SecondPartArrow
                                        key={index}
                                        direction={secondPart.direction}
                                        config={secondPart.config}
                                        text={
                                            secondPart.text ??
                                            translations.buildings[secondPart.config.building]?.navText ??
                                            ''
                                        }
                                    />
                                ))}

                                {productsConfig &&
                                    productsConfig.map((product, index) => (
                                        <StyledProductPin
                                            popoverOpenDefault={productParam === product.type}
                                            key={`${product.type}${index}`}
                                            productType={product.type}
                                            showNewFlag={products[product.type].new}
                                            className={classNames('animated', {
                                                fadeIn: pinsActive,
                                                active: pinsActive,
                                            })}
                                            coordinates={product.position}
                                            style={{
                                                ...getOffsetPosition({
                                                    x: product.position.x,
                                                    y: product.position.y,
                                                    imgWidth: state.appDimensions.width,
                                                    imgHeight: state.appDimensions.height,
                                                    imgOffsetX: isMobileDevice() ? 10 : 0,
                                                    imgOffsetY: isMobileDevice() ? 10 : 0,
                                                    imgScale: 1,
                                                }),
                                                animationDelay: `${index * 100}ms`,
                                            }}
                                        />
                                    ))}
                                {altProductsConfig && (
                                    <StyledAlternativeProductsPin
                                        alternativeProducts={altProductsConfig}
                                        currentAltProduct={currentAltProduct}
                                        onSelectAltProduct={setCurrentAltProduct}
                                        className={classNames('animated', {
                                            fadeIn: pinsActive,
                                            active: pinsActive,
                                        })}
                                        buildingType={type}
                                        style={{
                                            ...getOffsetPosition({
                                                x: altProductsConfig.position.x,
                                                y: altProductsConfig.position.y,
                                                imgWidth: state.appDimensions.width,
                                                imgHeight: state.appDimensions.height,
                                                imgOffsetX: 0,
                                                imgOffsetY: 0,
                                                imgScale: 1,
                                            }),
                                            animationDelay: `${100}ms`,
                                        }}
                                    />
                                )}
                            </PinchPan>
                            {!!sectors?.length && <BuildingNavigation buildingType={type} />}
                        </BuildingInner>
                    </CSSTransition>
                </SwitchTransition>
            </BuildingImage>
        </BuildingWrapper>
    );
})

export default BuildingDetails;
